import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AdditionalProduct } from '../../../shared/models/catalog/additional-product.model';
import { AdditionalProductService } from '../../../shared/services/catalog/additional-product.service';

@Component({
  selector: 'app-additional-products-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    CardModule,
    TableModule,
    ButtonModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './additional-products-page.component.html',
  styleUrl: './additional-products-page.component.scss',
})
export class AdditionalProductsPageComponent implements OnInit {
  service = inject(AdditionalProductService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #router = inject(Router);

  ngOnInit(): void {
    this.service.getAll();
  }

  navigateToCreate() {
    this.#router.navigate(['/catalog/additional-products/new']);
  }

  navigateToDetail(item: AdditionalProduct) {
    this.#router.navigate(['/catalog/additional-products', item.id]);
  }

  formatCurrentPrice(item: AdditionalProduct): string {
    if (!item.current_price) return '—';
    const value = parseFloat(item.current_price.price);
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  onRowDelete(event: Event, item: AdditionalProduct) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'catalog.additionalProducts.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'catalog.additionalProducts.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => this.#performDelete(item.id),
    });
  }

  #performDelete(id: number) {
    this.service.delete(id).subscribe({
      next: () => {
        this.service.getAll();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant('catalog.additionalProducts.deleted'),
        });
      },
      error: () => {
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            'catalog.additionalProducts.deleteError',
          ),
        });
      },
    });
  }
}
